import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  InputAdornment,
  ButtonGroup,
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  LocationOn as LocationIcon,
  LocalGasStation as GasStationIcon,
  Store as StoreIcon,
  Assessment as AssessmentIcon,
  Search as SearchIcon,
  MyLocation as MyLocationIcon,
  Map as MapIcon,
  TableChart as TableIcon,
} from '@material-ui/icons';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { socket } from '../SocketIo/Socket';

// Custom marker icons
const createColoredIcon = (color) => {
  return L.divIcon({
    className: 'custom-div-icon',
    html: `<div style="background-color:${color};width:14px;height:14px;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4);"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -10],
  });
};

const SVG_PATHS = {
  truck: "M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm12.5-1.5c0 .83-.67 1.5-1.5 1.5s-1.5-.67-1.5-1.5.67-1.5 1.5-1.5 1.5.67 1.5 1.5zM15 8H4V6h11v2z",
  person: "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z",
  location: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
};

const createSvgIcon = (type, color) => {
  const path = SVG_PATHS[type] || SVG_PATHS.location;
  
  return L.divIcon({
    className: 'svg-marker',
    html: `
      <svg width="30" height="30" viewBox="0 0 24 24" style="filter: drop-shadow(0px 2px 2px rgba(0,0,0,0.5));">
        <path d="${path}" fill="${color}" stroke="white" stroke-width="1"/>
      </svg>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15],
  });
};

const storeMarkerIcon = createColoredIcon('#FF9800');
const gasStationMarkerIcon = createColoredIcon('#F44336');
const otherMarkerIcon = createColoredIcon('#9C27B0');
const crewMovingIcon = createColoredIcon('#4CAF50');
const crewIdleIcon = createColoredIcon('#2196F3');

const vehicleIcon = createSvgIcon('truck', '#2196F3');
const crewIcon = createSvgIcon('person', '#4CAF50');
const locationIcon = createSvgIcon('location', '#FF9800');

const getLocationMarkerIcon = (type) => {
  switch (type) {
    case 'gas_station': return gasStationMarkerIcon;
    case 'convenience_store': return storeMarkerIcon;
    default: return otherMarkerIcon;
  }
};

const TILE_URLS = {
  satellite: 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
  street: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  statsCard: {
    height: '100%',
    position: 'relative',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[4],
    },
  },
  statsIcon: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    color: theme.palette.text.secondary,
    opacity: 0.3,
    fontSize: 48,
  },
  filterBar: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  actionButton: {
    marginLeft: theme.spacing(1),
  },
  brandChip: {
    margin: theme.spacing(0.5),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  mapContainer: {
    height: 600,
    width: '100%',
    marginTop: theme.spacing(2),
    borderRadius: 4,
    overflow: 'hidden',
    '& .leaflet-container': {
      height: '100%',
      width: '100%',
    },
  },
}));

const TrackedLocationsManager = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    type: '',
    brand: '',
    search: '',
  });
  const [filterOptions, setFilterOptions] = useState({ types: [], brands: [] });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [visitsDialogOpen, setVisitsDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const [selectedLocationVisits, setSelectedLocationVisits] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [mapLayer, setMapLayer] = useState('satellite');
  const [crewLocations, setCrewLocations] = useState([]);
  const [vehicles, setVehicles] =  useState([])
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const tileLayerRef = useRef(null);
  const locationMarkersRef = useRef([]);
  const crewMarkersRef = useRef({});
  const [formData, setFormData] = useState({
    name: '',
    type: 'convenience_store',
    brand: '',
    address: {
      street: '',
      city: '',
      state: '',
      zip: '',
    },
    location: {
      coordinates: [0, 0], // [lon, lat]
    },
    geofenceRadius: 50,
    notes: '',
  });

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/admin/tracked-locations');
      console.log('got locations...', response)
      setLocations(response.data.locations || []);
      setFilteredLocations(response.data.locations || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await axios.get('/api/admin/tracked-locations/meta/filters');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get('/api/admin/tracked-locations/reports/visits');
      console.log('Set stats', response)
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
    fetchFilterOptions();
    fetchStats();
  }, [fetchLocations, fetchFilterOptions, fetchStats]);

  // Fetch crew locations and subscribe to live updates
  useEffect(() => {
    if (!showMap) return;

    const fetchCrewLocations = async () => {
      try {
        const response = await axios.get('/api/crews/getLastLocation');
        console.log('fetchCrewLocations..', response)
        if (response?.data && response.data?.length) {
          setCrewLocations(response.data);
        }
      } catch (error) {
        console.error('Error fetching crew locations:', error);
      }
    };

    const fetchVehicleLastLocation = async () => {
      try {
        const response = await axios.get('/api/equipment/getVehicleLocations');
        console.log('fetchVehicleLastLocation..', response)
        if (response.data && response.data.length) {
          setVehicles(response.data);
        }
      } catch (error) {
        console.error('Error fetching vehicle locations:', error);
      }
    };

    fetchCrewLocations();
    fetchVehicleLastLocation() 
    const handleLocationUpdate = (data) => {
      setCrewLocations(prev => {
        const idx = prev.findIndex(c => c._id === data.crewId || c._id === data._id);
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = { ...updated[idx], ...data, lat: data.lat || data.latitude, lng: data.lng || data.longitude };
          return updated;
        }
        return prev;
      });
    };

    socket.on('crew:location:update', handleLocationUpdate);

    return () => {
      socket.off('crew:location:update', handleLocationUpdate);
    };
  }, [showMap]);

  // Initialize / destroy raw Leaflet map
  useEffect(() => {
    if (!showMap || !mapContainerRef.current) return;
    if (mapRef.current) return; // already initialized

    const map = L.map(mapContainerRef.current).setView([33.45, -111.95], 11);
    tileLayerRef.current = L.tileLayer(TILE_URLS[mapLayer], {
      attribution: mapLayer === 'satellite' ? '&copy; Google Maps' : '&copy; OpenStreetMap contributors',
      maxZoom: 20,
    }).addTo(map);
    mapRef.current = map;

    // Small delay to fix tile rendering
    setTimeout(() => map.invalidateSize(), 200);

    return () => {
      map.remove();
      mapRef.current = null;
      tileLayerRef.current = null;
      locationMarkersRef.current = [];
      crewMarkersRef.current = {};
    };
  }, [showMap]);

  // Swap tile layer when mapLayer changes
  useEffect(() => {
    if (!mapRef.current || !tileLayerRef.current) return;
    mapRef.current.removeLayer(tileLayerRef.current);
    tileLayerRef.current = L.tileLayer(TILE_URLS[mapLayer], {
      attribution: mapLayer === 'satellite' ? '&copy; Google Maps' : '&copy; OpenStreetMap contributors',
      maxZoom: 20,
    }).addTo(mapRef.current);
  }, [mapLayer]);

  // Update location markers when filteredLocations change
  useEffect(() => {
    if (!mapRef.current) return;
    // Clear old
    locationMarkersRef.current.forEach(m => m.remove());
    locationMarkersRef.current = [];

    const bounds = [];
    filteredLocations.forEach(loc => {
      if (!loc.location?.coordinates) return;
      const [lon, lat] = loc.location.coordinates;
      if (!lat || !lon) return;
      const marker = L.marker([lat, lon], { icon: getLocationMarkerIcon(loc.type) }).addTo(mapRef.current);
      const popupHtml = `<div>
        <strong>${loc.name || ''}</strong><br/>
        ${loc.brand ? `<em>${loc.brand}</em><br/>` : ''}
        <span>${(loc.type || '').replace(/_/g, ' ')}</span><br/>
        ${loc.address?.formatted ? `<span>${loc.address.formatted}</span><br/>` : ''}
        <span>Visits: ${loc.stats?.totalVisits || 0}</span>
      </div>`;
      marker.bindPopup(popupHtml);
      locationMarkersRef.current.push(marker);
      bounds.push([lat, lon]);
    });

    if (bounds.length > 0) {
      mapRef.current.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [filteredLocations, showMap]);

  // Update crew markers when crewLocations change
  useEffect(() => {
    if (!mapRef.current) return;

    // Remove old crew markers not in new data
    const newIds = new Set(crewLocations.map(c => c._id));
    Object.keys(crewMarkersRef.current).forEach(id => {
      if (!newIds.has(id)) {
        crewMarkersRef.current[id].remove();
        delete crewMarkersRef.current[id];
      }
    });
    vehicles.forEach(vehicle => {
      console.log('Display vehicle icons', vehicle)
      if (!vehicle.lat || !vehicle.lng) return;
    })
    crewLocations.forEach(crew => {
      if (!crew.lat || !crew.lng) return;
      const isMoving = crew.speed > 2;
      const icon = crewIcon // isMoving ? crewMovingIcon : crewIdleIcon;
      const popupHtml = `<div>
        <strong>${crew.name || 'Unknown Crew'}</strong><br/>
        ${crew.foremanName ? `Foreman: ${crew.foremanName}<br/>` : ''}
        Speed: ${crew.speed ? `${Math.round(crew.speed)} mph` : 'Idle'}<br/>
        ${crew.locationName ? `${crew.locationName}<br/>` : ''}
        ${crew.timestamp ? `<span style="font-size:0.85em;color:#666">Updated: ${new Date(crew.timestamp).toLocaleString()}</span>` : ''}
      </div>`;

      if (crewMarkersRef.current[crew._id]) {
        crewMarkersRef.current[crew._id].setLatLng([crew.lat, crew.lng]);
        crewMarkersRef.current[crew._id].setIcon(icon);
        crewMarkersRef.current[crew._id].setPopupContent(popupHtml);
      } else {
        const marker = L.marker([crew.lat, crew.lng], { icon }).addTo(mapRef.current);
        marker.bindPopup(popupHtml);
        crewMarkersRef.current[crew._id] = marker;
      }
    });
  }, [crewLocations, showMap]);

  // Apply filters
  useEffect(() => {
    let filtered = [...locations];

    if (filters.type) {
      filtered = filtered.filter(loc => loc.type === filters.type);
    }

    if (filters.brand) {
      filtered = filtered.filter(loc => loc.brand === filters.brand);
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(loc =>
        loc.name?.toLowerCase().includes(searchLower) ||
        loc.brand?.toLowerCase().includes(searchLower) ||
        loc.address?.formatted?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredLocations(filtered);
    setPage(0);
  }, [filters, locations]);

  const handleOpenDialog = (location = null) => {
    if (location) {
      setEditingLocation(location);
      setFormData({
        name: location.name || '',
        type: location.type || 'convenience_store',
        brand: location.brand || '',
        address: location.address || { street: '', city: '', state: '', zip: '' },
        location: location.location || { coordinates: [0, 0] },
        geofenceRadius: location.geofenceRadius || 50,
        notes: location.notes || '',
      });
    } else {
      setEditingLocation(null);
      setFormData({
        name: '',
        type: 'convenience_store',
        brand: '',
        address: { street: '', city: '', state: '', zip: '' },
        location: { coordinates: [0, 0] },
        geofenceRadius: 50,
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingLocation(null);
  };

  const handleSaveLocation = async () => {
    try {
      if (editingLocation) {
        await axios.put(`/api/admin/tracked-locations/${editingLocation._id}`, formData);
      } else {
        await axios.post('/api/admin/tracked-locations', formData);
      }
      handleCloseDialog();
      fetchLocations();
      fetchStats();
    } catch (error) {
      console.error('Error saving location:', error);
      alert('Error saving location: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteClick = (location) => {
    setLocationToDelete(location);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`/api/admin/tracked-locations/${locationToDelete._id}`);
      setDeleteDialogOpen(false);
      setLocationToDelete(null);
      fetchLocations();
      fetchStats();
    } catch (error) {
      console.error('Error deleting location:', error);
      alert('Error deleting location: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleViewVisits = async (location) => {
    try {
      const response = await axios.get(`/api/admin/tracked-locations/${location._id}/visits`);
      setSelectedLocationVisits(response.data);
      setVisitsDialogOpen(true);
    } catch (error) {
      console.error('Error fetching visits:', error);
      alert('Error fetching visits: ' + (error.response?.data?.error || error.message));
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num || 0);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'gas_station':
        return <GasStationIcon />;
      case 'convenience_store':
        return <StoreIcon />;
      default:
        return <LocationIcon />;
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h4" gutterBottom>
          Tracked Locations Manager
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Manage convenience stores, gas stations, and other tracked locations
        </Typography>
      </div>

      {/* Statistics */}
      {stats && (
        <Grid container spacing={3} style={{ marginBottom: 24 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statsCard}>
              <LocationIcon className={classes.statsIcon} />
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Locations
                </Typography>
                <Typography variant="h5">
                  {formatNumber(stats.totalLocations)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statsCard}>
              <AssessmentIcon className={classes.statsIcon} />
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Visits
                </Typography>
                <Typography variant="h5">
                  {formatNumber(stats.totalVisits)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statsCard}>
              <GasStationIcon className={classes.statsIcon} />
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Gas Stations
                </Typography>
                <Typography variant="h5">
                  {formatNumber(
                    locations.filter(loc => loc.type === 'gas_station').length
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statsCard}>
              <StoreIcon className={classes.statsIcon} />
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Convenience Stores
                </Typography>
                <Typography variant="h5">
                  {formatNumber(
                    locations.filter(loc => loc.type === 'convenience_store').length
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filter Bar */}
      <Paper className={classes.filterBar}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              placeholder="Search locations..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              >
                <MenuItem value="">All Types</MenuItem>
                {filterOptions.types.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Brand</InputLabel>
              <Select
                value={filters.brand}
                onChange={(e) => setFilters({ ...filters, brand: e.target.value })}
              >
                <MenuItem value="">All Brands</MenuItem>
                {filterOptions.brands.map(brand => (
                  <MenuItem key={brand} value={brand}>{brand}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={2}>
            <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Add Location
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={showMap ? <TableIcon /> : <MapIcon />}
                onClick={() => setShowMap(!showMap)}
              >
                {showMap ? 'Table View' : 'Map View'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Map View */}
      {showMap && (
        <Paper className={classes.mapContainer} style={{ position: 'relative' }}>
          <Box style={{ position: 'absolute', top: 10, right: 10, zIndex: 1000 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => setMapLayer(mapLayer === 'satellite' ? 'street' : 'satellite')}
              style={{ backgroundColor: '#fff', color: '#333', minWidth: 'auto', padding: '4px 10px', fontSize: 12 }}
            >
              {mapLayer === 'satellite' ? 'Street' : 'Satellite'}
            </Button>
          </Box>
          <div ref={mapContainerRef} style={{ height: '100%', width: '100%' }} />
        </Paper>
      )}

      {/* Locations Table */}
      {!showMap && <Paper>
        <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Locations ({formatNumber(filteredLocations.length)})
          </Typography>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchLocations();
              fetchStats();
            }}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer className={classes.tableContainer}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell align="center">Visits</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLocations
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((location) => (
                      <TableRow key={location._id}>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            {getTypeIcon(location.type)}
                            <Box ml={1}>{location.name}</Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={location.type}
                            className={classes.brandChip}
                          />
                        </TableCell>
                        <TableCell>{location.brand || '-'}</TableCell>
                        <TableCell>{location.address?.formatted || '-'}</TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            onClick={() => handleViewVisits(location)}
                          >
                            {formatNumber(location.stats?.totalVisits || 0)}
                          </Button>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(location)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(location)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredLocations.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </Paper>}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingLocation ? 'Edit Location' : 'Add New Location'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Location Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className={classes.formField}
            required
          />

          <FormControl fullWidth className={classes.formField}>
            <InputLabel>Type</InputLabel>
            <Select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            >
              <MenuItem value="convenience_store">Convenience Store</MenuItem>
              <MenuItem value="gas_station">Gas Station</MenuItem>
              <MenuItem value="restaurant">Restaurant</MenuItem>
              <MenuItem value="rest_area">Rest Area</MenuItem>
              <MenuItem value="truck_stop">Truck Stop</MenuItem>
              <MenuItem value="office">Office</MenuItem>
              <MenuItem value="warehouse">Warehouse</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Brand (e.g., QuickTrip, Circle K)"
            value={formData.brand}
            onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
            className={classes.formField}
          />

          <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
            Address
          </Typography>

          <TextField
            fullWidth
            label="Street"
            value={formData.address.street}
            onChange={(e) =>
              setFormData({
                ...formData,
                address: { ...formData.address, street: e.target.value },
              })
            }
            className={classes.formField}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.address.city}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    address: { ...formData.address, city: e.target.value },
                  })
                }
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="State"
                value={formData.address.state}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    address: { ...formData.address, state: e.target.value },
                  })
                }
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="ZIP"
                value={formData.address.zip}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    address: { ...formData.address, zip: e.target.value },
                  })
                }
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
            Location Coordinates
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Latitude"
                type="number"
                value={formData.location.coordinates[1]}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    location: {
                      coordinates: [
                        formData.location.coordinates[0],
                        parseFloat(e.target.value) || 0,
                      ],
                    },
                  })
                }
                inputProps={{ step: 'any' }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Longitude"
                type="number"
                value={formData.location.coordinates[0]}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    location: {
                      coordinates: [
                        parseFloat(e.target.value) || 0,
                        formData.location.coordinates[1],
                      ],
                    },
                  })
                }
                inputProps={{ step: 'any' }}
              />
            </Grid>
          </Grid>

          <TextField
            fullWidth
            label="Geofence Radius (meters)"
            type="number"
            value={formData.geofenceRadius}
            onChange={(e) =>
              setFormData({ ...formData, geofenceRadius: parseInt(e.target.value) || 50 })
            }
            className={classes.formField}
            style={{ marginTop: 16 }}
          />

          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={3}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            className={classes.formField}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveLocation} color="primary" variant="contained">
            {editingLocation ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Location</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Are you sure you want to delete "{locationToDelete?.name}"? This will mark it as inactive.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="secondary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Visits Dialog */}
      <Dialog
        open={visitsDialogOpen}
        onClose={() => setVisitsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Location Visits - {selectedLocationVisits?.location?.name}
        </DialogTitle>
        <DialogContent>
          {selectedLocationVisits && (
            <>
              <Box mb={2}>
                <Typography variant="subtitle2">
                  Total Visits: {formatNumber(selectedLocationVisits.count)}
                </Typography>
                {selectedLocationVisits.stats?.lastVisitDate && (
                  <Typography variant="body2" color="textSecondary">
                    Last Visit: {new Date(selectedLocationVisits.stats.lastVisitDate).toLocaleString()}
                  </Typography>
                )}
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date/Time</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Crew/Vehicle</TableCell>
                      <TableCell>Duration</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedLocationVisits.visits.slice(0, 50).map((visit, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {new Date(visit.timestamp).toLocaleString()}
                        </TableCell>
                        <TableCell>{visit.type}</TableCell>
                        <TableCell>
                          {visit.crew || visit.vehicle || visit.user || '-'}
                        </TableCell>
                        <TableCell>
                          {visit.durationMinutes
                            ? `${visit.durationMinutes.toFixed(0)} min`
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {selectedLocationVisits.visits.length > 50 && (
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                  Showing first 50 of {selectedLocationVisits.visits.length} visits
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVisitsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TrackedLocationsManager;
